import axios from "axios";
import endpoints from "endpoints";
import queryClient from "queryClient";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";

export type IGetPatientListQueryParams = {
    limit: number;
    offset?: number;
    keyword?: string;
};

export type IPatient = {
    createdDate: Date;
    patientId: number;
    zohoId: string;
    patientname: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: any;
    avatarPath?: any;
    isActive: number;
    phoneNumber: string;
    address?: any;
    deleteFlag: number;
    isRegistered: number;
    zugangscode: string;
    operationDate: string;
    grafts: number;
    methode: string;
    bereich: string;
    imagesRecords: any;
    dateOfBirth: string;
    ankunftsdatum: string;
    abflugsdatum: string;
    abflugzeit: string;
    ankunftszeit: string;
    abflugflugnummer: string;
    ankunftsflugnummer: string;
    devices: any;
    languages: Array<string>;
};

export type IPatientListResponse<T> = {
    status: number;
    message: string;
    data: T;
};

export type IPatientList = {
    limit: number;
    offset: number;
    more: boolean;
    count: number;
    patients: IPatient[];
    total: number;
};

export type IEvent = {
    id: number;
    title: string;
    details: {
        type: string;
        iconName: string;
        indicatorType: string;
        local_notification: {
            id: number;
            time: string;
            event_id: number;
            notification_text: string;
            notification_title: string;
        }[];
    };
    content: null;
    onDay: number;
    isClickable: boolean;
    condition: string;
    showAppPopup: boolean;
    phase: number;
    isFired: boolean;
    isTask: boolean;
    hasLocalNotification: boolean;
    eventDate: string;
    eventPopup: string;
    prerequisiteEvents: null;
    bound_events: null;
};

export type IPatientEvent = {
    createdDate: string;
    id: number;
    eventId: number;
    patientId: number;
    isFired: boolean;
    isDone: string;
    dateTimeZone: string;
};

export type IModifiedPatientEventWithEventDetails = {
    title: string;
    phase: number;
};

type UpdatePatientParams = Pick<IPatient, "patientId" | "firstName" | "lastName">;

const getPatientById = async (id: IPatient["patientId"]): Promise<IPatient> => {
    return axios
        .get<IPatientListResponse<IPatient>>(`${endpoints.getPatientById}/${id}`)
        .then((response) => response.data.data);
};

const listPatientEvents = async (id: IPatient["patientId"]): Promise<IPatientEvent[]> => {
    return axios
        .get<IPatientListResponse<IPatientEvent[]>>(`${endpoints.listPatientEvents}/${id}`)
        .then((response) => response.data.data);
};

const getEventsData = async (): Promise<IEvent[]> => {
    return axios.get<IPatientListResponse<IEvent[]>>(endpoints.getEventsData).then((response) => response.data.data);
};

export const listPatients = async (queryParams: IGetPatientListQueryParams): Promise<IPatientList> => {
    return axios
        .get<
            IPatientListResponse<IPatientList>
        >(`${endpoints.listPatients}limit=${queryParams.limit ?? 0}&offset=${queryParams.offset ?? 0}&keyword=${queryParams.keyword ?? ""}`)
        .then((response) => response.data.data);
};

const updatePatient = async (values: UpdatePatientParams): Promise<IPatient> => {
    return axios
        .put<IPatientListResponse<IPatient>>(`${endpoints.updatePatient}/${values.patientId}`, values)
        .then((response) => response.data.data);
};

export const sendPatientResetPasswordEmail = async (email: string): Promise<any> => {
    return axios
        .post<IPatientListResponse<any>>(endpoints.sendPatientResetPasswordEmail, { email })
        .then((response) => response.data.data);
};

export const usePatientsList = (
    queryParams: IGetPatientListQueryParams,
    paginationModel: { page: number; pageSize: number }
) =>
    useQuery<IPatientList>([endpoints.listPatients, paginationModel.page, paginationModel.pageSize], () =>
        listPatients(queryParams)
    );

export const useEventData = () => useQuery<IEvent[]>([endpoints.getEventsData], () => getEventsData());

export const usePatientData = (patientId: IPatient["patientId"]) =>
    useQuery<IPatient>([endpoints.getPatientById, patientId], () => getPatientById(patientId));

export const useListPatientEvents = (patientId: IPatient["patientId"]) =>
    useQuery<IPatientEvent[]>(endpoints.listPatientEvents, () => listPatientEvents(patientId));

export const useUpdatePatient = () =>
    useMutation<IPatient, Error, UpdatePatientParams>({
        mutationFn: updatePatient,
        onSuccess: (newItem) => {
            queryClient.setQueryData<IPatient[]>(endpoints.listPatients, (oldData = []) =>
                oldData.map((item) => (item.patientId === newItem.patientId ? newItem : item))
            );
            toast.success(`Updated successfully`);
        },
    });
